<template>
  <help-dialog title="About Workflows" content-class="dialog-top-right">
    <template #activator="{ openDialog }">
      <v-btn
        variant="text"
        icon="mds:help"
        class="mr-4"
        @click="openDialog"
      />
    </template>

    <template #default>
      <h3 class="mb-1 text-black">Workflow</h3>
      <p class="text-grey-darken-3">
        A workflow is a sequence of <b>Events</b> arranged on a timeline. It supports various approaches, such as:
      </p>
      <br>
      <ol class="pl-10 text-black">
        <li>
          <a
            href="https://www.qlerify.com/event-storming-tool"
            target="_blank"
            class="text-blue-accent-4"
          >
            <b>Event Storming</b>
          </a>
        </li>
        <li>
          <a
            href="https://www.qlerify.com/event-modeling-tool"
            target="_blank"
            class="text-blue-accent-4"
          >
            <b>Event Modeling</b>
          </a>
        </li>
        <li>
          <a
            href="https://www.qlerify.com/process-mapping-tool"
            target="_blank"
            class="text-blue-accent-4"
          >
            <b>Simple Process Mapping</b>
          </a>
        </li>
        <li>
          <a
            href="https://www.qlerify.com/user-story-mapping"
            target="_blank"
            class="text-blue-accent-4"
          >
            <b>User Story Mapping</b>
          </a>
        </li>
        <li>
          <a
            href="https://www.qlerify.com/domain-driven-design-tool"
            target="_blank"
            class="text-blue-accent-4"
          >
            <b>Domain Driven Design (DDD)</b>
          </a>
        </li>
      </ol>
      <br>
      Click on the links above to learn more about each approach.
      <br><br>

      <h3 class="mb-1 text-black">Events</h3>
      <p class="text-grey-darken-3">
        An <b>Event</b> is something important that happens in your organization—typically
        described with a past-tense verb and a noun like “Order Created” or “Broken Equipment
        Detected.” Events can be triggered by people (internal or external), systems, or time.
        <br><br>
        We recommend capturing only <b>state-changing</b> events—those that changes information in a system.
        For example, “Guest viewed calendar for room availability” is <b>not</b> considered an event unless it changes the system’s state.
        <br><br>
        Events might also be referred to as steps in a process, tasks in a user journey, or Scrum epics.
      </p>

      <br><br>

      <h3 class="mb-1 text-black">Lanes</h3>
      <p class="text-grey-darken-3">
        Lanes indicate the primary <b>Actor</b> for each <b>Event</b> in the workflow.
        An Actor (such as a user or a system) initiates a <b>Command</b> that leads to an Event.
        For example, in “Customer created order,” the Actor is “Customer,” and “created order” is the Event.
      </p>

      <br><br>

      <h3 class="mb-1 text-black">Cards</h3>
      <p class="text-grey-darken-3">
        Cards let you add details—such as user stories, test cases, problems, or opportunities—to each Event.
        You can add multiple Cards to an Event, and each Card can include comments, attached files, and reactions.
        <br><br>
        Use <b>workflow settings</b> (via the tool icon <v-icon size="x-small">mdi-cog-outline</v-icon> at the top right) to configure available Card types.
        Cards of card types marked with <b>“Show in backlog”</b> appear in the backlog, where they can be reordered to reflect priority.
        <br><br>
        There are three special card types used to build the domain model:
      </p>
      <br>
      <ol class="pl-10 text-black">
        <li><b>Command:</b> The name of the command an Actor initiates</li>
        <li><b>Aggregate Root:</b> The name of the Aggregate Root for this command</li>
        <li><b>Read Model:</b> The name of the query that provides information to the actor</li>
      </ol>
      <br>
      An Event can have <b>multiple</b> Read Models, but only <b>one</b> Command and <b>one</b> Aggregate Root.

      <br><br>

      <h3 class="mb-1 text-black">Backlog & User Story Map</h3>
      <p class="text-grey-darken-3">
        There are two ways to work with the backlog, both located under the workflow:
      </p>
      <br>
      <ol class="pl-10 text-black">
        <li><b>Backlog –</b> Shows all cards, that are configured to be shown in the backlog, in order of priority.</li>
        <li><b>User Story Map –</b> Cards are displayed in a horizontal layout aligned with their corresponding events.</li>
      </ol>
      <br>
      <p class="text-grey-darken-3">
        Both views display only Cards belonging to Card types configured as <b>“Show in backlog”</b>.
        You can assign releases by selecting a number in the <b>Release</b> column in <b>Backlog</b>
        or by hovering over a Card and using the context menu in the <b>User Story Map</b>. Or by using drag and drop.
      </p>

      <br><br>

      <h3 class="mb-1 text-black">Release Filter</h3>
      <p class="text-grey-darken-3">
        Apply a visual filter to the entire workflow to highlight the scope for each iteration.
        Simply use the <b>Filter</b> dropdown above the workflow to select which releases you want to view.
      </p>

      <br><br>

      <h3 class="mb-1 text-black">Domain Model</h3>
      <p class="text-grey-darken-3">
        The <b>Domain Model</b> section, located under the workflow, allows you to visualize your domain model and group <b>Entities</b> into <b>Bounded Contexts</b>.
        This section includes detailed help texts to guide you through the process.
      </p>

      <br><br>

      <h3 class="mb-1">History</h3>
      <p class="text-grey-darken-3">
        The History section displays a record of the latest updates made to the workflow and allows you to roll back to previous versions.
      </p>

      <br><br>

      <h3 class="mb-1">AI</h3>
      <p class="text-grey-darken-3">
        Use AI to quickly create any kind of workflow, enriched with the cards of your choice.
        AI can either create a workflow from scratch or add individual steps, cards, comments, read models and commands to an existing workflow.
        To create a complete workflow with AI, start from an empty workflow and click the "<b>Generate Workflow with AI</b>" button.
      </p>
    </template>
  </help-dialog>
</template>

<script>
import HelpDialog from '@/components/HelpDialog.vue'

export default {
  components: {
    HelpDialog
  }
}
</script>
